import {
  type FunctionComponent,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import IconClose from 'assets/images/icon-cross-24px.svg';
import IconNotificationReply from 'assets/images/icon-notification-reply.svg';
import IconNotificationTips from 'assets/images/icon-notification-tips.svg';

import './style.scss';

type NotificationKindT = 'REPLY' | 'TIPS';

type NotificationT = {
  category: 'GENERAL' | 'UPDATE';
  id: string;
  kind: NotificationKindT;
  read: boolean;
  text: string;
  time: string;
  title: string;
};

const INITIAL_NOTIFICATIONS: NotificationT[] = [
  {
    category: 'GENERAL',
    id: '1',
    kind: 'TIPS',
    read: false,
    text: 'Keep videos between 2-8 minutes for better engagement.',
    time: '2h ago',
    title: 'Content Tips',
  },
  {
    category: 'GENERAL',
    id: '2',
    kind: 'REPLY',
    read: false,
    text: 'Support has responded to your request.',
    time: '3h ago',
    title: 'Support Reply',
  },
];

const KIND_ICONS: Record<NotificationKindT, typeof IconNotificationTips> = {
  REPLY: IconNotificationReply,
  TIPS: IconNotificationTips,
};

type FilterT = 'ALL' | 'READ' | 'UNREAD' | 'UPDATES';

const FILTERS: Array<{ label: string; value: FilterT }> = [
  { label: 'All', value: 'ALL' },
  { label: 'Read', value: 'READ' },
  { label: 'Unread', value: 'UNREAD' },
  { label: 'Updates', value: 'UPDATES' },
];

type AnchorRectT = {
  right: number;
  top: number;
};

type PropsT = {
  anchorRef: RefObject<HTMLElement | null>;
  onClose: () => void;
};

const NotificationsPanel: FunctionComponent<PropsT> = ({
  anchorRef,
  onClose,
}) => {
  const [notifications, setNotifications] = useState(INITIAL_NOTIFICATIONS);
  const [filter, setFilter] = useState<FilterT>('ALL');
  const [anchorRect, setAnchorRect] = useState<AnchorRectT>();

  const panelRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (rect) {
      setAnchorRect({
        right: window.innerWidth - rect.right,
        top: rect.bottom + 8,
      });
    }
  }, [anchorRef]);

  useEffect(() => {
    const onDocPointerDown = (e: PointerEvent): void => {
      const target = e.target as Node;
      if (
        !panelRef.current?.contains(target)
        && !anchorRef.current?.contains(target)
      ) onClose();
    };
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown);
    };
  }, [anchorRef, onClose]);

  const visibleNotifications = notifications.filter((notification) => {
    switch (filter) {
      case 'ALL': return true;
      case 'READ': return notification.read;
      case 'UNREAD': return !notification.read;
      case 'UPDATES': return notification.category === 'UPDATE';
      default: throw Error('Unexpected case');
    }
  });

  return (
    <div
      ref={panelRef}
      style={anchorRect ? {
        right: `${anchorRect.right}px`,
        top: `${anchorRect.top}px`,
      } : undefined}
      styleName="panel"
    >
      <div styleName="header">
        <h2 styleName="title">Notifications</h2>
        <button
          aria-label="Close notifications"
          onClick={onClose}
          styleName="closeButton"
          type="button"
        >
          <IconClose />
        </button>
      </div>

      <div styleName="pills">
        {FILTERS.map(({ label, value }) => (
          <button
            key={value}
            onClick={() => {
              setFilter(value);
            }}
            styleName={filter === value ? 'pill active' : 'pill'}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>

      <div styleName="listHeader">
        <span>Today</span>
        <button
          onClick={() => {
            setNotifications(
              (prev) => prev.map((n) => ({ ...n, read: true })),
            );
          }}
          styleName="markAllRead"
          type="button"
        >
          Mark all as read
        </button>
      </div>

      <div styleName="list">
        {visibleNotifications.length ? visibleNotifications.map((
          notification,
        ) => {
          const Icon = KIND_ICONS[notification.kind];
          return (
            <div key={notification.id} styleName="card">
              <div styleName="icon"><Icon /></div>
              <div styleName="content">
                <div styleName="cardHeader">
                  <span styleName="cardTitle">{notification.title}</span>
                  <span styleName="cardTime">{notification.time}</span>
                </div>
                <p styleName="cardText">{notification.text}</p>
              </div>
            </div>
          );
        }) : <div styleName="empty">No notifications to show.</div>}
      </div>
    </div>
  );
};

export default NotificationsPanel;
